var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Schema, type } from "@colyseus/schema";
/**
 * A pickable item lying in the shared world. These live in `GameState.groundItems`
 * and are synced to every client (public world state), mirroring the `Player`
 * pattern. They are runtime-only — never persisted — and are re-seeded when the
 * room is (re)created.
 *
 * NOTE: like `state.ts`, this file is compiled with `tsc` (legacy decorators) and
 * must NOT be transformed by esbuild/Vite directly — consumers import `dist/`.
 */
export class GroundItem extends Schema {
    constructor() {
        super(...arguments);
        /** One of the shared `ITEM_IDS`, or `CASH_DROP_ID` for a dropped pile of cash. */
        this.itemId = "";
        this.x = 0;
        this.y = 0;
        /** For a cash drop (`itemId === CASH_DROP_ID`): the cash amount; credited to the wallet on pickup. */
        this.amount = 0;
    }
}
__decorate([
    type("string"),
    __metadata("design:type", Object)
], GroundItem.prototype, "itemId", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], GroundItem.prototype, "x", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], GroundItem.prototype, "y", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], GroundItem.prototype, "amount", void 0);
/** A shared, timed teleport portal anyone nearby can step through until it expires. */
export class PortalNode extends Schema {
    constructor() {
        super(...arguments);
        this.name = "";
        /** Zone the portal physically sits in (only visible/usable there). */
        this.zone = "";
        /** Destination zone + spawn (server px). */
        this.destZone = "";
        this.destX = 0;
        this.destY = 0;
        this.x = 0;
        this.y = 0;
        /** Seconds remaining before it collapses (server counts down). */
        this.secondsLeft = 60;
    }
}
__decorate([
    type("string"),
    __metadata("design:type", Object)
], PortalNode.prototype, "name", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], PortalNode.prototype, "zone", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], PortalNode.prototype, "destZone", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], PortalNode.prototype, "destX", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], PortalNode.prototype, "destY", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], PortalNode.prototype, "x", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], PortalNode.prototype, "y", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], PortalNode.prototype, "secondsLeft", void 0);
/** A mineable rock node in the city's mine area (server-authoritative, shared). */
export class MineNode extends Schema {
    constructor() {
        super(...arguments);
        /** One of the shared `MINE_KINDS` (gold/silver/iron/stone). */
        this.kind = "stone";
        this.x = 0;
        this.y = 0;
        /** Remaining mines before the node is depleted (starts at MINE_NODE_HITS). */
        this.hitsLeft = 5;
    }
}
__decorate([
    type("string"),
    __metadata("design:type", Object)
], MineNode.prototype, "kind", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], MineNode.prototype, "x", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], MineNode.prototype, "y", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], MineNode.prototype, "hitsLeft", void 0);
/** A weed plant growing in the Kapon wilds (server-authoritative, shared). Planted from a Black
 *  Market seed; grows through 4 stages, each needing one watering, and rots if left thirsty too
 *  long. `growth`/`water`/`dryLeftMs` are display values the server's plant tick refreshes ~1/s
 *  so every client's status panel shows the same bars. Runtime-only (like chests/portals). */
export class WeedPlant extends Schema {
    constructor() {
        super(...arguments);
        /** Planter's character id — ONLY caps plants per planter; the plant itself belongs to no one
         *  (anyone may water/harvest/clear it, and the panel shows no name). */
        this.owner = "";
        this.x = 0;
        this.y = 0;
        /** Growth stage 1..WEED_STAGES. */
        this.stage = 1;
        /** "thirsty" (needs a watering to grow) · "growing" · "ready" (harvest!) · "rotten" (dead). */
        this.status = "thirsty";
        /** Overall growth percent (0..100) across all stages. */
        this.growth = 0;
        /** The CURRENT stage's water remaining, percent (100 right after watering → 0 as it's used up). */
        this.water = 0;
        /** While thirsty: ms left before the plant rots (drives the panel's countdown). 0 otherwise. */
        this.dryLeftMs = 0;
    }
}
__decorate([
    type("string"),
    __metadata("design:type", Object)
], WeedPlant.prototype, "owner", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], WeedPlant.prototype, "x", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], WeedPlant.prototype, "y", void 0);
__decorate([
    type("uint8"),
    __metadata("design:type", Object)
], WeedPlant.prototype, "stage", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], WeedPlant.prototype, "status", void 0);
__decorate([
    type("uint8"),
    __metadata("design:type", Object)
], WeedPlant.prototype, "growth", void 0);
__decorate([
    type("uint8"),
    __metadata("design:type", Object)
], WeedPlant.prototype, "water", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], WeedPlant.prototype, "dryLeftMs", void 0);
/** A tuft of cuttable grass in the city (server-authoritative, shared). Cut one (with a
 *  grass-cutter) for cash; it vanishes. New tufts trickle in over time up to a cap. */
export class GrassNode extends Schema {
    constructor() {
        super(...arguments);
        /** Visual variant index (0..GRASS_VARIANTS-1). */
        this.variant = 0;
        this.x = 0;
        this.y = 0;
    }
}
__decorate([
    type("number"),
    __metadata("design:type", Object)
], GrassNode.prototype, "variant", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], GrassNode.prototype, "x", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], GrassNode.prototype, "y", void 0);
//# sourceMappingURL=items.js.map